import React, { useContext, useEffect, useState, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import InnerLayout from "../../components/layouts/inner-layout";
import { DataContext } from "../../components/layouts/main-layout";
import TabsComponent from "../../components/helper-components/tabs-component";
import SelectField from "../../components/forms/select-field";
import AddButton from "../../components/buttons/button";
import CloseIcon from "../../assets/icons/close-icon";
import successToast from "../../components/helper-components/success-toast";
import errorToast from "../../components/helper-components/error-toast";
import EditIcon from "../../assets/icons/action-icons";
import DeleteIcon from "../../assets/icons/delete-icon";
import {
  zonalHeadZonesApi,
  zonalHeadListApi,
  assignZonalHeadApi,
  updateZonalHeadApi,
  deleteZonalHeadApi,
  zonalHeadUserListApi,
} from "../../redux/apis/coco-zonal-head-api";

// The four zones are fixed on the backend (South | North | West | East). They are
// listed here so the Zone dropdown still works if the /zones reference call fails;
// the state lists for each zone come from that call.
const ZONES = ["North", "South", "East", "West"];

const zoneTabs = ["All Zones", ...ZONES];

// GET /zones returns [{ zone, states: [...] }]; older builds returned a plain
// { South: [...] } map. Normalise both into the map the dropdowns read.
const toZoneStateMap = (raw) => {
  if (Array.isArray(raw)) {
    return raw.reduce((map, entry) => {
      if (entry?.zone) map[entry.zone] = entry.states ?? entry.state ?? [];
      return map;
    }, {});
  }
  if (raw && typeof raw === "object") return raw;
  return {};
};

// This backend answers with HTTP 200 even when the call fails, carrying the real
// outcome in the body as { status: false, message }. Axios resolves those, so
// they must be checked explicitly or a failure reports as success.
const failureMessage = (res) =>
  res?.data?.status === false ? res?.data?.message || "Request failed" : null;

const headings = [
  { label: "ZONAL HEAD" },
  { label: "ZONE" },
  { label: "STATE" },
  { label: "STATUS" },
  { label: "ACTIONS" },
];

const cellClass =
  "border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)] whitespace-nowrap";

const schema = Yup.object().shape({
  user_id: Yup.string().required("Zonal Head is required"),
  zone: Yup.string().required("Zone is required"),
});

// The list endpoint's rows may nest the user, so read the display name from
// whichever shape comes back rather than assuming one.
const userNameOf = (item) =>
  item?.user_name ??
  item?.user?.user_name ??
  item?.user?.name ??
  item?.user_id ??
  "--";

const CocoZoneAssignment = () => {
  const { permissionFn } = useContext(DataContext);

  const [currentTab, setCurrentTab] = useState("All Zones");
  const [assignments, setAssignments] = useState([]);
  const [zoneStates, setZoneStates] = useState({});
  const [userOptions, setUserOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  // The zonal-head routes are deployed per environment. Where they are absent the
  // backend answers "Page not found", so the page says so plainly instead of
  // rendering an empty table that looks like "no assignments yet".
  const [routesUnavailable, setRoutesUnavailable] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAssignments = (zone) => {
    setLoading(true);
    // Deleting an assignment is a soft delete (is_active = false). Those rows are
    // kept for audit but must not appear here, so they are filtered out at the
    // API — and again on the client, so a row can never slip through.
    zonalHeadListApi({
      zone: zone === "All Zones" ? "" : zone,
      is_active: true,
    })
      .then((res) => {
        const failed = failureMessage(res);
        if (failed) {
          // A missing route is an environment problem, not a user error — it is
          // reported in the page body rather than as a transient toast.
          if (/page not found/i.test(failed)) {
            setRoutesUnavailable(true);
          } else {
            errorToast(failed);
          }
          setAssignments([]);
          return;
        }
        setRoutesUnavailable(false);
        const raw = res.data?.data ?? res.data;
        const list = Array.isArray(raw) ? raw : raw?.list ?? [];
        setAssignments(list.filter((item) => item?.is_active !== false));
      })
      .catch((err) => {
        errorToast(
          err?.response?.data?.message ?? "Failed to load zone assignments"
        );
        setAssignments([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAssignments(currentTab);
  }, [currentTab]);

  useEffect(() => {
    zonalHeadZonesApi()
      .then((res) => {
        setZoneStates(toZoneStateMap(res.data?.data ?? res.data));
      })
      .catch((err) => {
        // Without this map the State dropdown silently has no options, so make
        // the failure visible rather than leaving it looking empty-by-design.
        console.error(
          "Failed to fetch zone/state map:",
          err?.response?.data ?? err?.message
        );
        setZoneStates({});
      });
  }, []);

  useEffect(() => {
    zonalHeadUserListApi()
      .then((res) => {
        const raw = res.data?.data ?? res.data;
        const list = Array.isArray(raw) ? raw : raw?.list ?? raw?.userList ?? [];
        setUserOptions(
          list
            .filter((u) => u?.user_id)
            .map((u) => ({ label: u.user_name ?? u.user_id, value: u.user_id }))
        );
      })
      .catch(() => setUserOptions([]));
  }, []);

  const handleDelete = (item) => {
    if (
      !window.confirm(
        `Deactivate the Zonal Head assignment for ${item.state ?? item.zone}? It is kept for audit but stops routing approvals.`
      )
    )
      return;
    deleteZonalHeadApi(item.id)
      .then((res) => {
        const failed = failureMessage(res);
        if (failed) {
          errorToast(failed);
          return;
        }
        successToast(res.data?.message ?? "Assignment deactivated");
        fetchAssignments(currentTab);
      })
      .catch((err) => {
        errorToast(
          err?.response?.data?.message ?? "Failed to deactivate assignment"
        );
      });
  };

  const closePanel = () => {
    setShowPanel(false);
    setEditing(null);
  };

  // On create, several states can be assigned at once; on update the API takes a
  // single state, so the control switches between multi and single accordingly.
  const isEdit = !!editing;

  const initialValues = {
    user_id: editing?.user_id ?? "",
    zone: editing?.zone ?? "",
    state: isEdit ? editing?.state ?? "" : [],
    is_active: editing?.is_active ?? true,
  };

  const stateOptionsFor = (zone) =>
    (zoneStates?.[zone] ?? []).map((s) => ({ label: s, value: s }));

  const apiZones = Object.keys(zoneStates);
  const zoneSelectOptions = (apiZones.length ? apiZones : ZONES).map((z) => ({
    label: z,
    value: z,
  }));

  const onSubmit = (values, { setSubmitting, resetForm }) => {
    const selectedStates = isEdit
      ? values.state || null
      : (values.state ?? []).map((s) => s?.value ?? s);

    const request = isEdit
      ? updateZonalHeadApi({
          id: editing.id,
          user_id: values.user_id,
          zone: values.zone,
          state: selectedStates || null,
          is_active: values.is_active,
        })
      : assignZonalHeadApi({
          user_id: values.user_id,
          zone: values.zone,
          state: selectedStates.length ? selectedStates : null,
        });

    request
      .then((res) => {
        // Keep the panel open on failure so the input is not lost.
        const failed = failureMessage(res);
        if (failed) {
          errorToast(failed);
          return;
        }
        const data = res.data?.data ?? {};
        const created = data.created?.length ?? 0;
        // The API skips states already actively assigned in the zone rather than
        // erroring, which otherwise looks like the states silently vanished.
        // Name them so it is obvious which ones did not take.
        const skippedNames = (data.skipped ?? [])
          .map((s) => s?.state ?? s?.name ?? s)
          .filter(Boolean);

        if (isEdit) {
          successToast(res.data?.message ?? "Assignment updated");
        } else if (created && skippedNames.length) {
          successToast(
            `${created} assigned. Already assigned to another Zonal Head, so skipped: ${skippedNames.join(", ")}`
          );
        } else if (!created && skippedNames.length) {
          errorToast(
            `Already assigned to another Zonal Head: ${skippedNames.join(", ")}`
          );
        } else {
          successToast(
            created > 1
              ? `${created} assignments created`
              : res.data?.message ?? "Zonal Head assigned"
          );
        }

        fetchAssignments(currentTab);
        closePanel();
        resetForm();
      })
      .catch((err) => {
        errorToast(
          err?.response?.data?.message ?? "Failed to save zone assignment"
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <InnerLayout
      permissionFn={permissionFn}
      module="coco_zone_assignment"
      title="COCO Zone Assignment"
    >
      <div className="flex flex-col h-full">
        <div className="px-5 xl:px-10 pt-5">
          <TabsComponent
            handleClickfn={(item) => setCurrentTab(item)}
            itemTabs={zoneTabs}
            setCurrentTab={setCurrentTab}
            currentTab={currentTab}
          />
        </div>

        <div className="px-5 xl:px-10 pt-5 flex flex-col gap-4 flex-1 overflow-hidden">
          <div className="flex justify-end">
            {!routesUnavailable &&
              permissionFn("coco_zone_assignment")?.create && (
              <AddButton
                text="Assign Zonal Head"
                onclkFn={() => {
                  setEditing(null);
                  setShowPanel(true);
                }}
                prefixIcon={true}
              />
            )}
          </div>

          <div className="tab-container outline outline-[var(--list-table-border-color)] outline-1 border-radius overflow-auto">
            <table className="w-full border-collapse mt-0">
              <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
                <tr className="h-[50px]">
                  {headings.map((h, i) => (
                    <th
                      key={i}
                      className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]"
                    >
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={headings.length}
                      className="px-[8px] h-[50px] text-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : routesUnavailable ? (
                  <tr>
                    <td
                      colSpan={headings.length}
                      className="px-[8px] py-6 text-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]"
                    >
                      Zone assignment is not available on this environment yet —
                      the <code>/coco-store/zonal-head</code> endpoints are not
                      deployed here. Assignments cannot be listed or saved until
                      the backend is released.
                    </td>
                  </tr>
                ) : assignments.length === 0 ? (
                  <tr>
                    <td
                      colSpan={headings.length}
                      className="px-[8px] h-[50px] text-center text-[var(--list-table-row-fg-color)] text-[length:--list-table-row-font-size]"
                    >
                      No zone assignments found.
                    </td>
                  </tr>
                ) : (
                  assignments.map((item, idx) => (
                    <tr key={item.id ?? idx} className="hover:bg-[var(--list-table-hover-bg)]">
                      <td className={cellClass}>{userNameOf(item)}</td>
                      <td className={cellClass}>{item.zone ?? "--"}</td>
                      <td className={cellClass}>
                        {item.state ? item.state : "Whole zone"}
                      </td>
                      <td className={cellClass}>
                        {item.is_active === false ? "Inactive" : "Active"}
                      </td>
                      <td className={cellClass}>
                        <div className="flex items-center gap-3">
                          {permissionFn("coco_zone_assignment")?.update && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditing(item);
                                setShowPanel(true);
                              }}
                            >
                              <EditIcon />
                            </button>
                          )}
                          {permissionFn("coco_zone_assignment")?.delete &&
                            item.is_active !== false && (
                              <DeleteIcon onClickFn={() => handleDelete(item)} />
                            )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Assign / Edit — right slide panel */}
      <Transition.Root show={showPanel} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePanel}>
          <div className="fixed inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
            </Transition.Child>

            <div className="fixed inset-0 flex justify-end">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="h-full relative w-[420px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                  <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    validationSchema={schema}
                    onSubmit={onSubmit}
                  >
                    {({ isSubmitting, values, setFieldValue }) => (
                      <Form className="flex flex-col h-full">
                        <div
                          className="h-[61px] bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b flex-shrink-0"
                          style={{ borderColor: "var(--slide-panel-border)" }}
                        >
                          <span
                            style={{
                              color: "var(--slide-panel-h-fg)",
                              fontSize: "var(--slide-panel-h-fs)",
                              fontWeight: "var(--slide-panel-h-fw)",
                            }}
                          >
                            {isEdit ? "Edit Zone Assignment" : "Assign Zonal Head"}
                          </span>
                          <button type="button" onClick={closePanel}>
                            <CloseIcon />
                          </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-5 py-5">
                          <SelectField
                            name="user_id"
                            id="user_id"
                            label="Zonal Head"
                            placeholder="Select Zonal Head"
                            options={userOptions}
                            isRequired={true}
                            isSearchable={true}
                            labelWidth="9rem"
                            errMsgContainerClass="ps-2"
                          />
                          <SelectField
                            name="zone"
                            id="zone"
                            label="Zone"
                            placeholder="Select Zone"
                            options={zoneSelectOptions}
                            isRequired={true}
                            labelWidth="9rem"
                            errMsgContainerClass="ps-2"
                            handleSelected={() =>
                              setFieldValue("state", isEdit ? "" : [])
                            }
                          />
                          <SelectField
                            name="state"
                            id="state"
                            label="State"
                            placeholder={
                              values.zone
                                ? "Leave empty for the whole zone"
                                : "Select a zone first"
                            }
                            options={stateOptionsFor(values.zone)}
                            isMulti={!isEdit}
                            isClearable={true}
                            isDisabled={!values.zone}
                            labelWidth="9rem"
                            errMsgContainerClass="ps-2"
                          />

                          {isEdit && (
                            <div className="mb-2 flex items-center w-full">
                              <label
                                style={{ minWidth: "9rem" }}
                                className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]"
                              >
                                Active
                              </label>
                              <Field name="is_active">
                                {({ field, form }) => (
                                  <input
                                    type="checkbox"
                                    checked={!!field.value}
                                    onChange={(e) =>
                                      form.setFieldValue("is_active", e.target.checked)
                                    }
                                  />
                                )}
                              </Field>
                            </div>
                          )}

                          <p className="text-[length:var(--input-place-font-size)] text-[var(--input-place-color)] mt-3 leading-relaxed">
                            A store routes to the Zonal Head assigned to its state.
                            If no state-specific head exists, the whole-zone head is
                            used instead.
                          </p>
                        </div>

                        <div className="px-5 py-4 border-t flex justify-end flex-shrink-0">
                          <AddButton
                            prefixIcon={false}
                            text={isEdit ? "Update" : "Assign"}
                            type="submit"
                            loading={isSubmitting}
                          />
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </InnerLayout>
  );
};

export default CocoZoneAssignment;
